import { type ReactElement, useState } from 'react';
import { cn } from '@/shared/ui';

export interface ProductSummaryProps {
  /** 상품 대표 사진 주소 — null 이면 사진이 없다. */
  readonly imageUrl: string | null;
  readonly productName: string;
  readonly colorName: string;
  /** 놓일 자리(그리드 칸) — 폼이 정한다. */
  readonly className?: string;
}

/**
 * 일반 견적 요청 화면의 상품 정보(일반 견적 설계) — 디자인이 없어 디자인 썸네일 대신 상품 대표 사진과 상품·색상을 보여 준다.
 * 칸 모양은 견적 요청 폼의 디자인 정보(`DesignThumbnail` 정사각 회색 칸)와 같다. 사진이 없거나 불러오지 못하면 "상품 사진 없음".
 * 디자인 썸네일의 "미리보기 준비 중"은 그릴 그림이 곧 생긴다는 뜻이라 여기서는 쓰지 않는다.
 */
export const ProductSummary = ({
  imageUrl,
  productName,
  colorName,
  className,
}: ProductSummaryProps): ReactElement => {
  const [failedUrl, setFailedUrl] = useState<string | null>(null);

  return (
    <section
      aria-label="상품 정보"
      className={cn(
        'flex items-center gap-5 border-b border-foreground/10 pb-6 lg:border-b-0 lg:pb-0',
        className,
      )}
    >
      <div className="relative aspect-square size-28 shrink-0 overflow-hidden bg-muted/70 sm:size-32 xl:size-40">
        {imageUrl !== null && failedUrl !== imageUrl ? (
          <img
            src={imageUrl}
            alt={`${productName} 사진`}
            loading="lazy"
            decoding="async"
            className="size-full object-contain"
            onError={() => setFailedUrl(imageUrl)}
          />
        ) : (
          <p className="flex size-full items-center justify-center p-2 text-center text-xs text-balance break-keep text-muted-foreground">
            상품 사진 없음
          </p>
        )}
      </div>
      <div className="flex min-w-0 flex-col gap-2">
        <p className="text-xs text-muted-foreground">상품 · 색상</p>
        <p className="text-lg leading-snug font-semibold tracking-[-0.01em] wrap-anywhere">{`${productName} · ${colorName}`}</p>
      </div>
    </section>
  );
};
